/**
 * Material-style press ripples, installed once for the whole app.
 *
 * Chrome's own touch feedback is a translucent blue rectangle that ignores
 * border radius and squares off every pill and card, which is the single
 * clearest tell that something is a web page rather than an app. It is turned
 * off in styles.css; this is what replaces it.
 *
 * ONE delegated listener rather than a component or a hook per button. Every
 * tappable thing in this app is already a `<button>`, so a component would mean
 * touching thirty call sites to change how a press looks, and the next button
 * anyone adds would silently not have it. Opt-OUT is the right default here:
 * new buttons get the app's feel for free.
 *
 * The ripple starts where the finger actually landed. That is the whole point —
 * a fixed centre-out animation reads as a canned effect, while one that begins
 * under your fingertip reads as the surface responding to you.
 */

/**
 * The bottom nav opts out.
 *
 * A ripple needs `overflow: hidden` to be clipped to the control, and the nav's
 * unread badge is deliberately positioned outside its icon's box — clipping it
 * would silently eat the count. It has its own press animation already.
 */
const OPT_OUT = ".navitem, .no-ripple";

const ANIMATION_MS = 520;

export function installRipple(doc: Document = document): () => void {
  const reduced = doc.defaultView?.matchMedia("(prefers-reduced-motion: reduce)");

  const onDown = (e: PointerEvent) => {
    // Respected live, not read once at startup: someone can turn it on mid-session.
    if (reduced?.matches) return;
    if (!(e.target instanceof Element)) return;

    const host = e.target.closest<HTMLElement>("button, [role='button']");
    if (!host || host.matches(OPT_OUT)) return;
    if (host.hasAttribute("disabled") || host.getAttribute("aria-disabled") === "true") {
      return;
    }

    const rect = host.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Reach the furthest corner from the touch, so the ripple always covers the
    // whole control however near the edge you press.
    const radius = Math.hypot(Math.max(x, rect.width - x), Math.max(y, rect.height - y));

    const ink = doc.createElement("span");
    ink.className = "ripple";
    ink.setAttribute("aria-hidden", "true");
    ink.style.width = `${radius * 2}px`;
    ink.style.height = `${radius * 2}px`;
    ink.style.left = `${x - radius}px`;
    ink.style.top = `${y - radius}px`;

    host.appendChild(ink);
    // `animationend` alone would leak a node if the element is hidden mid-press
    // — a screen change during the animation is exactly when that happens.
    const drop = () => ink.remove();
    ink.addEventListener("animationend", drop, { once: true });
    doc.defaultView?.setTimeout(drop, ANIMATION_MS + 120);
  };

  // Capture phase: a handler that calls stopPropagation must not cost the user
  // their press feedback.
  doc.addEventListener("pointerdown", onDown, true);
  return () => doc.removeEventListener("pointerdown", onDown, true);
}
